/**
 * Fleet Grid Summary — aggregate grid import/export across all properties.
 * Shows daily totals (7-day trend) and per-property breakdown.
 * Data source: get_analytics_fleet_summary RPC (server-side rollup, org-scoped).
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useApp } from '@/contexts/AppContext';
import { useFleetAnalytics } from '@/hooks/useFleetAnalytics';

/** Inline bar showing relative magnitude. */
function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="h-1.5 w-16 rounded-full bg-border/40 overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

export function FleetGridSummary({ days = 7 }: { days?: number }) {
  const { organization, overrideOrgId } = useApp();
  const orgId = overrideOrgId ?? organization?.id;
  const today = new Date().toISOString().slice(0, 10);
  const start = new Date(Date.now() - (days - 1) * 86400000).toISOString().slice(0, 10);

  const { data: fa, isLoading, error } = useFleetAnalytics(orgId, start, today);

  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium text-secondary">Fleet Grid Summary</CardTitle>
        </CardHeader>
        <CardContent className="py-6 flex items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-secondary" />
        </CardContent>
      </Card>
    );
  }

  if (error || !fa || fa.property_count === 0 || !fa.daily_trend?.length) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2 pt-3 px-4">
          <CardTitle className="text-sm font-medium text-secondary">Fleet Grid Summary</CardTitle>
        </CardHeader>
        <CardContent className="py-4 px-4">
          <p className="text-xs text-muted-foreground">No grid data available for the last {days} days.</p>
        </CardContent>
      </Card>
    );
  }

  // Daily totals from the RPC's daily_trend (values are kWh, sorted desc by date)
  const dailyTotals = [...fa.daily_trend]
    .map((d) => ({ date: d.local_date, imported: d.grid_import_kwh, exported: d.grid_export_kwh }))
    .sort((a, b) => b.date.localeCompare(a.date));

  // Per-property totals from the RPC's properties array (values are kWh)
  const propertyTotals = (fa.properties ?? [])
    .map((p) => ({ id: p.id, name: p.name, imported: p.grid_import_kwh, exported: p.grid_export_kwh }))
    .sort((a, b) => (b.imported + b.exported) - (a.imported + a.exported));

  const periodImported = fa.fleet_grid_import_kwh;
  const periodExported = fa.fleet_grid_export_kwh;
  const periodNet = periodImported - periodExported;

  const maxDailyImport = Math.max(...dailyTotals.map((d) => d.imported));
  const maxDailyExport = Math.max(...dailyTotals.map((d) => d.exported));
  const maxPropImport = Math.max(...propertyTotals.map((p) => p.imported), 0);
  const maxPropExport = Math.max(...propertyTotals.map((p) => p.exported), 0);

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-3 pt-3 px-4">
        <CardTitle className="text-sm font-medium text-secondary">Fleet Grid Summary ({days}-day)</CardTitle>
      </CardHeader>
      <CardContent className="px-4 pb-4 space-y-5">
        {/* Period summary cards */}
        <div className="grid grid-cols-3 gap-3">
          <div className="rounded-md bg-wt-bg-primary/50 border border-border/50 px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <ArrowDownLeft className="h-3.5 w-3.5 text-wt-amber-primary" />
              <span className="text-xs text-muted-foreground">Imported</span>
            </div>
            <span className="font-mono text-xl font-semibold text-wt-amber-primary">{periodImported.toFixed(1)}</span>
            <span className="text-xs text-muted-foreground ml-1">kWh</span>
          </div>
          <div className="rounded-md bg-wt-bg-primary/50 border border-border/50 px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <ArrowUpRight className="h-3.5 w-3.5 text-wt-green-primary" />
              <span className="text-xs text-muted-foreground">Exported</span>
            </div>
            <span className="font-mono text-xl font-semibold text-wt-green-primary">{periodExported.toFixed(1)}</span>
            <span className="text-xs text-muted-foreground ml-1">kWh</span>
          </div>
          <div className="rounded-md bg-wt-bg-primary/50 border border-border/50 px-3 py-2.5">
            <div className="flex items-center gap-1.5 mb-1">
              <span className="text-xs text-muted-foreground">Net</span>
            </div>
            <span className={`font-mono text-xl font-semibold ${periodNet <= 0 ? 'text-wt-green-primary' : 'text-wt-amber-primary'}`}>
              {periodNet >= 0 ? '+' : ''}{periodNet.toFixed(1)}
            </span>
            <span className="text-xs text-muted-foreground ml-1">kWh</span>
          </div>
        </div>

        {/* Two-column layout: daily + per-property */}
        <div className="grid grid-cols-2 gap-4">
          {/* Daily trend */}
          <div>
            <p className="text-xs text-muted-foreground mb-2 font-medium">Daily totals</p>
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left font-normal pb-1.5">Date</th>
                  <th className="text-right font-normal pb-1.5 w-20">Import</th>
                  <th className="w-16 pb-1.5" />
                  <th className="text-right font-normal pb-1.5 w-20">Export</th>
                  <th className="w-16 pb-1.5" />
                </tr>
              </thead>
              <tbody>
                {dailyTotals.map((d) => (
                  <tr key={d.date} className="border-t border-border/30">
                    <td className="text-muted-foreground py-1.5">{d.date}</td>
                    <td className="text-right text-wt-amber-primary py-1.5">{d.imported.toFixed(1)}</td>
                    <td className="py-1.5 pl-2"><MiniBar value={d.imported} max={maxDailyImport} color="bg-wt-amber-primary" /></td>
                    <td className="text-right text-wt-green-primary py-1.5">{d.exported.toFixed(1)}</td>
                    <td className="py-1.5 pl-2"><MiniBar value={d.exported} max={maxDailyExport} color="bg-wt-green-primary" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Per-property breakdown */}
          <div>
            <p className="text-xs text-muted-foreground mb-2 font-medium">By property ({days}-day total)</p>
            <table className="w-full text-sm font-mono">
              <thead>
                <tr className="text-xs text-muted-foreground">
                  <th className="text-left font-normal pb-1.5">Property</th>
                  <th className="text-right font-normal pb-1.5 w-20">Import</th>
                  <th className="w-16 pb-1.5" />
                  <th className="text-right font-normal pb-1.5 w-20">Export</th>
                  <th className="w-16 pb-1.5" />
                </tr>
              </thead>
              <tbody className="max-h-56">
                {propertyTotals.map((p) => (
                  <tr key={p.id} className="border-t border-border/30">
                    <td className="text-muted-foreground py-1.5 truncate max-w-[180px]">{p.name}</td>
                    <td className="text-right text-wt-amber-primary py-1.5">{p.imported.toFixed(1)}</td>
                    <td className="py-1.5 pl-2"><MiniBar value={p.imported} max={maxPropImport} color="bg-wt-amber-primary" /></td>
                    <td className="text-right text-wt-green-primary py-1.5">{p.exported.toFixed(1)}</td>
                    <td className="py-1.5 pl-2"><MiniBar value={p.exported} max={maxPropExport} color="bg-wt-green-primary" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
